import { Injectable } from '@angular/core';
import type { Content } from 'pdfmake/interfaces';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import {
  ReportPdfExportOptions,
  ReportPdfExportService,
} from './report-pdf-export.service';
import {
  StatisticsKpi,
  StatisticsPdfExportContext,
  StatisticsTabKey,
  StatisticsExportSnapshot,
  StatisticsPdfImageSegment,
  StatisticsPdfTableRow,
  StatisticsExportProgressCallback,
  ALARM_CENTER_SIDEBAR_PDF_GROUP,
} from './statistics-export.models';
import { isExportBackgroundPixel } from './statistics-export-capture.service';

/** Landscape A4 width in PDF points. */
const LANDSCAPE_A4_WIDTH_PT = 842;
const LANDSCAPE_A4_HEIGHT_PT = 595;
/** Match reports PDF margins for consistent usable area. */
export const STATISTICS_PDF_PAGE_MARGINS: [number, number, number, number] = [40, 48, 40, 48];
/** Landscape A4 content area in PDF points (page minus margins). */
const PDF_CONTENT_WIDTH =
  LANDSCAPE_A4_WIDTH_PT - STATISTICS_PDF_PAGE_MARGINS[0] - STATISTICS_PDF_PAGE_MARGINS[2];
const PDF_CONTENT_HEIGHT =
  LANDSCAPE_A4_HEIGHT_PT - STATISTICS_PDF_PAGE_MARGINS[1] - STATISTICS_PDF_PAGE_MARGINS[3];
const PDF_WIDGET_TITLE_SPACE = 32;
const PDF_MAX_IMAGE_HEIGHT = PDF_CONTENT_HEIGHT - PDF_WIDGET_TITLE_SPACE;
const PDF_SIDEBAR_COLUMN_GAP_PT = 16;
/** Left column share of the combined alarm-center sidebar page. */
const PDF_SIDEBAR_LEFT_COLUMN_RATIO = 0.36;
/** Max square pie size (pt) on the alarm-center sidebar EMAAC card. */
const EMAAC_SIDEBAR_PIE_MAX_PT = 200;
const EMAAC_SIDEBAR_CARD_FILL = '#e5e8ef';
/** pdfmake canvas fill colors for the EMAAC pie legend (must be hex, not rgb()). */
const EMAAC_LEGEND_COLOR_EMA = '#FF764A';
const EMAAC_LEGEND_COLOR_FINISHED = '#2C3F68';
/** Embed 2 captured pixels per PDF point for sharper output. */
const PDF_PIXEL_RATIO = 2;
/** Minimum slice height (px at capture scale) to emit a PDF page segment. */
const MIN_SEGMENT_CONTENT_HEIGHT_PX = 24;
/** Search window (px) when snapping a page break away from text rows. */
const SLICE_BOUNDARY_SEARCH_PX = 96;
/** Hard cap on image load + canvas processing per widget; prevents a hung export. */
const PDF_IMAGE_PREPARE_TIMEOUT_MS = 15000;

const TAB_TITLE_KEYS: Record<StatisticsTabKey, string> = {
  alerts: 'ALERTS_TAB',
  alarmCenter: 'ALARM_CENTER',
  careRecipients: 'CARE_RECIPIENTS',
  activeUsers: 'ACTIVE_USERS',
};

@Injectable({
  providedIn: 'root',
})
export class StatisticsPdfExportService {
  constructor(
    private reportPdfExport: ReportPdfExportService,
    private translate: TranslateService
  ) {}

  buildFilename(context: StatisticsPdfExportContext): string {
    const startDate = this.formatStatisticsDate(context.startDate);
    const endDate = this.formatStatisticsDate(context.endDate);
    return `statistics-${context.tab}-${startDate}_${endDate}.pdf`;
  }

  async export(
    context: StatisticsPdfExportContext,
    onProgress?: StatisticsExportProgressCallback
  ): Promise<void> {
    const options = await this.buildExportOptions(context, onProgress);
    onProgress?.({
      phase: 'generate',
      current: 1,
      total: 1,
      messageKey: 'LOADING_PDF',
    });
    const blob = await this.reportPdfExport.generateBlob(options);
    await this.reportPdfExport.savePdfBlob(blob, options.filename, undefined, {
      preferBrowserDownload: true,
    });
  }

  /** Exposed for unit tests. */
  buildTitlePageContent(context: StatisticsPdfExportContext): Content[] {
    const startDateTime = this.formatStatisticsDateTime(context.startDate);
    const endDateTime = this.formatStatisticsDateTime(context.endDate);

    const blocks: Content[] = [
      {
        text: `${this.translate.instant('TIME_TEXT')}: ${context.dateOptionText}`,
        style: 'subtitle',
        margin: [0, 0, 0, 2],
      },
      {
        text: `${this.translate.instant('REPORT_START_DATE')}: ${startDateTime} — ${this.translate.instant('REPORT_END_DATE')}: ${endDateTime} (UTC)`,
        style: 'subtitle',
        margin: [0, 0, 0, 8],
      },
      { text: this.translate.instant('FILTERS'), style: 'sectionTitle' },
    ];

    if (context.filterLines.length === 0) {
      blocks.push({ text: '—', style: 'filterLine' });
    } else {
      for (const line of context.filterLines) {
        blocks.push({ text: `${line.label}: ${line.value}`, style: 'filterLine' });
      }
    }

    blocks.push({ text: this.translate.instant('STATISTICS'), style: 'sectionTitle', margin: [0, 12, 0, 4] });
    blocks.push(...this.buildKpiLines(context.kpis));

    return blocks;
  }

  buildWidgetPages(snapshots: StatisticsExportSnapshot[]): Content[] {
    const pages: Content[] = [];
    let index = 0;

    while (index < snapshots.length) {
      const snapshot = snapshots[index];
      if (snapshot.pdfPageGroup === ALARM_CENTER_SIDEBAR_PDF_GROUP) {
        const group: StatisticsExportSnapshot[] = [];
        while (
          index < snapshots.length &&
          snapshots[index].pdfPageGroup === ALARM_CENTER_SIDEBAR_PDF_GROUP
        ) {
          group.push(snapshots[index]);
          index++;
        }
        const combinedPage = this.buildAlarmCenterSidebarPage(group);
        if (combinedPage) {
          pages.push(combinedPage);
        } else {
          console.warn(
            `[statistics-pdf-export] alarm-center sidebar group produced no page; omitting ${group.length} snapshot(s): ${group
              .map((s) => s.slug)
              .join(', ')}`
          );
        }
        continue;
      }

      if (snapshot.pdfNativeTable) {
        pages.push(this.buildNativeTablePage(snapshot));
        index++;
        continue;
      }

      if (snapshot.avgDurations) {
        pages.push(this.buildAvgDurationsPage(snapshot));
        index++;
        continue;
      }

      const segments = this.resolvePdfSegments(snapshot).filter((segment) =>
        this.isValidImageDataUrl(segment.imageDataUrl)
      );
      if (!segments.length) {
        console.warn(
          `[statistics-pdf-export] no valid image segments for widget "${snapshot.slug}"; omitting it from the PDF`
        );
        index++;
        continue;
      }

      segments.forEach((segment, segmentIndex) => {
        pages.push(this.buildSingleImageWidgetPage(snapshot, segment, segmentIndex === 0));
      });
      index++;
    }

    return pages;
  }

  /** Exposed for unit tests. */
  buildAlarmCenterSidebarPage(group: StatisticsExportSnapshot[]): Content | null {
    const careRecipients = group.find((s) => s.slug === 'care-recipient-counter');
    const responseTime = group.find((s) => s.slug === 'response-time-statistics');
    const emaacAlerts = group.find((s) => s.slug === 'emaac-alerts');

    if (!careRecipients && !responseTime && !emaacAlerts) {
      return null;
    }

    const leftColumnWidth = Math.floor(
      (PDF_CONTENT_WIDTH - PDF_SIDEBAR_COLUMN_GAP_PT) * PDF_SIDEBAR_LEFT_COLUMN_RATIO
    );
    const rightColumnWidth = PDF_CONTENT_WIDTH - PDF_SIDEBAR_COLUMN_GAP_PT - leftColumnWidth;

    const leftBlocks: Content[] = [];
    if (careRecipients) {
      leftBlocks.push(...this.buildColumnWidgetBlocks(careRecipients, leftColumnWidth));
    }
    if (responseTime) {
      if (leftBlocks.length) {
        leftBlocks.push({ text: '', margin: [0, 12, 0, 0] });
      }
      leftBlocks.push(...this.buildColumnWidgetBlocks(responseTime, leftColumnWidth));
    }

    const rightBlocks = emaacAlerts
      ? emaacAlerts.pdfNativeLayout === 'emaac-alerts-sidebar'
        ? this.buildEmaacSidebarPdfBlocks(emaacAlerts, rightColumnWidth)
        : this.buildColumnWidgetBlocks(
            emaacAlerts,
            rightColumnWidth,
            PDF_MAX_IMAGE_HEIGHT,
            PDF_SIDEBAR_COLUMN_GAP_PT
          )
      : [];

    this.scaleLeftColumnBlocksToFit(leftBlocks, PDF_MAX_IMAGE_HEIGHT);

    if (!leftBlocks.length && !rightBlocks.length) {
      return null;
    }

    return {
      pageBreak: 'before',
      columnGap: PDF_SIDEBAR_COLUMN_GAP_PT,
      columns: [
        { width: leftColumnWidth, stack: leftBlocks.length ? leftBlocks : [{ text: '' }] },
        { width: rightColumnWidth, stack: rightBlocks.length ? rightBlocks : [{ text: '' }] },
      ],
      unbreakable: true,
    };
  }

  private buildColumnWidgetBlocks(
    snapshot: StatisticsExportSnapshot,
    maxWidthPt: number,
    maxHeightPt?: number,
    horizontalMarginPt = 0
  ): Content[] {
    const segment = this.resolvePdfSegments(snapshot).find((s) =>
      this.isValidImageDataUrl(s.imageDataUrl)
    );
    if (!segment) {
      return [];
    }

    const contentWidth = Math.max(1, maxWidthPt - horizontalMarginPt * 2);
    const fitted = this.scaleSegmentToFit(segment, contentWidth, maxHeightPt);
    const blocks: Content[] = [];

    if (!snapshot.omitPdfTitle) {
      blocks.push({
        text: this.translate.instant(snapshot.titleKey),
        style: 'widgetTitle',
        margin: [horizontalMarginPt, 0, horizontalMarginPt, 6],
      });
    }

    blocks.push({
      image: fitted.imageDataUrl,
      fit: [fitted.pdfWidth, fitted.pdfHeight],
      margin: [horizontalMarginPt, 0, horizontalMarginPt, 0],
    });

    return blocks;
  }

  /** Exposed for unit tests. */
  buildEmaacSidebarPdfBlocks(snapshot: StatisticsExportSnapshot, maxWidthPt: number): Content[] {
    const data = snapshot.emaacSidebarData;
    const pieImage = snapshot.squareChartImageDataUrl ?? snapshot.imageDataUrl;
    if (!data || !this.isValidImageDataUrl(pieImage)) {
      return this.buildColumnWidgetBlocks(snapshot, maxWidthPt, PDF_MAX_IMAGE_HEIGHT, PDF_SIDEBAR_COLUMN_GAP_PT);
    }

    const cardPaddingPt = 14;
    // The table carries a left margin (== column gap) so its width must be reduced by that
    // amount; otherwise leftMargin + tableWidth exceeds maxWidthPt and pdfmake clips the
    // right edge of the grey card.  No right margin is added — the page margin provides the
    // visual boundary, and the pdfmake columnGap already separates the two columns.
    const tableWidth = maxWidthPt - PDF_SIDEBAR_COLUMN_GAP_PT;
    const innerWidth = Math.max(1, tableWidth - cardPaddingPt * 2);
    const pieSizePt = Math.min(innerWidth, EMAAC_SIDEBAR_PIE_MAX_PT);

    const cardStack: Content[] = [
      {
        text: this.translate.instant(snapshot.titleKey),
        style: 'widgetTitle',
        margin: [0, 0, 0, 4],
      },
      {
        text: String(data.totalAlerts),
        fontSize: 18,
        bold: true,
        color: '#07184D',
        margin: [0, 0, 0, 10],
      },
      {
        image: pieImage,
        fit: [pieSizePt, pieSizePt],
        alignment: 'center',
        margin: [0, 0, 0, 10],
      },
      // pdfmake canvas rects require hex colors; rgb() strings render as the same default fill.
      this.buildEmaacLegendRow(EMAAC_LEGEND_COLOR_EMA, `${data.legendLabels[0]}: ${data.emaAlerts}`),
      this.buildEmaacLegendRow(EMAAC_LEGEND_COLOR_FINISHED, `${data.legendLabels[1]}: ${data.finishedAlerts}`),
    ];

    return [
      {
        table: {
          widths: [tableWidth],
          body: [
            [
              {
                stack: cardStack,
                fillColor: EMAAC_SIDEBAR_CARD_FILL,
                margin: [cardPaddingPt, cardPaddingPt, cardPaddingPt, cardPaddingPt],
              },
            ],
          ],
        },
        layout: 'noBorders',
        margin: [PDF_SIDEBAR_COLUMN_GAP_PT, 0, 0, 0],
      },
    ];
  }

  private buildEmaacLegendRow(color: string, label: string): Content {
    const fillColor = this.normalizePdfCanvasColor(color);
    return {
      columns: [
        {
          width: 14,
          canvas: [{ type: 'rect', x: 0, y: 1, w: 10, h: 10, color: fillColor }],
        },
        {
          width: '*',
          text: label,
          fontSize: 10,
          bold: true,
          color: '#07184D',
          margin: [0, 1, 0, 0],
        },
      ],
      columnGap: 4,
      margin: [0, 0, 0, 4],
    };
  }

  private scaleLeftColumnBlocksToFit(leftBlocks: Content[], maxHeightPt: number): void {
    const imageBlocks = leftBlocks.filter(
      (block): block is { image: string; fit: [number, number] } =>
        typeof block === 'object' &&
        block !== null &&
        'image' in block &&
        Array.isArray((block as { fit?: unknown }).fit) &&
        (block as { fit: [number, number] }).fit.length === 2
    );

    const totalHeight = imageBlocks.reduce((sum, block) => sum + block.fit[1], 0);
    const spacerCount = leftBlocks.filter(
      (block) => typeof block === 'object' && block !== null && 'text' in block && !(block as { image?: string }).image
    ).length;
    const spacerHeight = spacerCount * 12;
    const combined = totalHeight + spacerHeight;

    if (combined <= maxHeightPt || totalHeight <= 0) {
      return;
    }

    const scale = (maxHeightPt - spacerHeight) / totalHeight;
    imageBlocks.forEach((block) => {
      block.fit = [block.fit[0] * scale, block.fit[1] * scale];
    });
  }

  /** Exposed for unit tests. */
  scaleSegmentToFit(
    segment: StatisticsPdfImageSegment,
    maxWidthPt: number,
    maxHeightPt?: number
  ): StatisticsPdfImageSegment {
    let pdfWidth = segment.pdfWidth;
    let pdfHeight = segment.pdfHeight;

    if (pdfWidth > maxWidthPt) {
      const widthScale = maxWidthPt / pdfWidth;
      pdfWidth = maxWidthPt;
      pdfHeight *= widthScale;
    }

    if (maxHeightPt && pdfHeight > maxHeightPt) {
      const heightScale = maxHeightPt / pdfHeight;
      pdfHeight = maxHeightPt;
      pdfWidth *= heightScale;
    }

    return {
      imageDataUrl: segment.imageDataUrl,
      pdfWidth,
      pdfHeight,
    };
  }

  /** Exposed for unit tests. */
  isValidImageDataUrl(dataUrl: string | undefined | null): boolean {
    return (
      !!dataUrl &&
      dataUrl.startsWith('data:image') &&
      dataUrl.length > 'data:image/png;base64,'.length + 8
    );
  }

  /** Exposed for unit tests. */
  computePdfLayoutAtWidth(
    imageWidthPx: number,
    imageHeightPx: number,
    maxWidthPt = PDF_CONTENT_WIDTH
  ): { pdfWidth: number; pdfHeight: number; scale: number } {
    const scale = Math.min(1, (maxWidthPt * PDF_PIXEL_RATIO) / imageWidthPx);
    const outputWidthPx = Math.max(1, Math.round(imageWidthPx * scale));
    const outputHeightPx = Math.max(1, Math.round(imageHeightPx * scale));

    return {
      scale,
      pdfWidth: outputWidthPx / PDF_PIXEL_RATIO,
      pdfHeight: outputHeightPx / PDF_PIXEL_RATIO,
    };
  }

  /** Exposed for unit tests. Scale up or down to fill the PDF content box. */
  computePdfLayoutFitPage(
    imageWidthPx: number,
    imageHeightPx: number,
    maxWidthPt = PDF_CONTENT_WIDTH,
    maxHeightPt = PDF_MAX_IMAGE_HEIGHT
  ): { pdfWidth: number; pdfHeight: number; scale: number } {
    const maxWidthPx = maxWidthPt * PDF_PIXEL_RATIO;
    const maxHeightPx = maxHeightPt * PDF_PIXEL_RATIO;
    let scale = maxWidthPx / Math.max(1, imageWidthPx);
    let outputHeightPx = imageHeightPx * scale;

    if (outputHeightPx > maxHeightPx) {
      scale = maxHeightPx / Math.max(1, imageHeightPx);
    }

    const outputWidthPx = Math.max(1, Math.round(imageWidthPx * scale));
    outputHeightPx = Math.max(1, Math.round(imageHeightPx * scale));

    return {
      scale,
      pdfWidth: outputWidthPx / PDF_PIXEL_RATIO,
      pdfHeight: outputHeightPx / PDF_PIXEL_RATIO,
    };
  }

  /** Exposed for unit tests. */
  shouldSplitAcrossPages(pdfHeight: number): boolean {
    return pdfHeight > PDF_MAX_IMAGE_HEIGHT;
  }

  /**
   * Render a tabular widget as a native pdfmake table (vector text). Unlike the
   * image path this needs no html2canvas, stays crisp, and paginates
   * automatically — pdfmake repeats the header row when the table spans pages.
   */
  private buildNativeTablePage(snapshot: StatisticsExportSnapshot): Content {
    const table = snapshot.pdfNativeTable;
    if (!table) {
      return { text: '' };
    }

    const stack: Content[] = [];

    if (!snapshot.omitPdfTitle) {
      stack.push({
        text: this.translate.instant(snapshot.titleKey),
        style: 'widgetTitle',
        margin: [0, 0, 0, 6],
      });
    }

    if (table.headlineValue) {
      stack.push({
        text: table.headlineValue,
        fontSize: 18,
        bold: true,
        color: '#07184D',
        margin: [0, 0, 0, 10],
      });
    }

    // Lay the rows out across N side-by-side column groups (column-major, like a
    // CSS multi-column list) so long lists don't waste vertical space. A thin
    // empty spacer column separates adjacent groups.
    const groups = Math.max(1, table.columnGroups ?? 1);
    const cols = table.columns;
    const SPACER_WIDTH = 16;

    const headerCells: Content[] = [];
    const widths: (number | '*' | 'auto')[] = [];
    for (let g = 0; g < groups; g++) {
      if (g > 0) {
        headerCells.push({ text: '' });
        widths.push(SPACER_WIDTH);
      }
      for (const col of cols) {
        headerCells.push({
          text: this.translate.instant(col.headerKey),
          bold: true,
          fontSize: 10,
          color: '#07184D',
          alignment: col.align ?? 'left',
        });
        widths.push(col.width ?? 'auto');
      }
    }

    const columns = this.distributeRowsIntoColumns(table.rows, groups);
    const perColumn = columns.reduce((max, col) => Math.max(max, col.length), 0);
    const bodyRows: Content[][] = [];
    for (let i = 0; i < perColumn; i++) {
      const rowCells: Content[] = [];
      for (let g = 0; g < groups; g++) {
        if (g > 0) {
          rowCells.push({ text: '' });
        }
        const sourceRow = columns[g][i];
        for (let c = 0; c < cols.length; c++) {
          rowCells.push({
            text: sourceRow?.cells[c] ?? '',
            fontSize: 10,
            bold: sourceRow?.group ?? false,
            color: sourceRow?.group ? '#07184D' : undefined,
            alignment: cols[c].align ?? 'left',
            margin: c === 0 && sourceRow?.indent ? [12, 0, 0, 0] : undefined,
          });
        }
      }
      bodyRows.push(rowCells);
    }

    stack.push({
      table: {
        headerRows: 1,
        widths,
        body: [headerCells, ...bodyRows],
      },
      layout: 'lightHorizontalLines',
    });

    return {
      stack,
      pageBreak: 'before',
    };
  }

  /**
   * Average alert durations rendered natively: a horizontal stacked bar (drawn
   * with pdfmake canvas rects) plus the four KPI cards. No html2canvas.
   */
  private buildAvgDurationsPage(snapshot: StatisticsExportSnapshot): Content {
    const data = snapshot.avgDurations;
    if (!data) {
      return { text: '' };
    }

    const stack: Content[] = [];

    if (!snapshot.omitPdfTitle) {
      stack.push({
        text: this.translate.instant(snapshot.titleKey),
        style: 'widgetTitle',
        margin: [0, 0, 0, 8],
      });
    }

    stack.push({
      text: `${data.totalLabel}: ${data.totalValue}`,
      fontSize: 10,
      color: '#707070',
      alignment: 'right',
      margin: [0, 0, 0, 6],
    });

    // Stacked bar: one rect per segment, width proportional to its seconds.
    const barHeight = 26;
    const totalSeconds = data.segments.reduce((sum, seg) => sum + Math.max(0, seg.seconds), 0);
    const barElements: Record<string, unknown>[] = [];
    if (totalSeconds > 0) {
      let x = 0;
      for (const seg of data.segments) {
        const width = (Math.max(0, seg.seconds) / totalSeconds) * PDF_CONTENT_WIDTH;
        if (width <= 0) {
          continue;
        }
        barElements.push({ type: 'rect', x, y: 0, w: width, h: barHeight, color: seg.color });
        x += width;
      }
    } else {
      barElements.push({ type: 'rect', x: 0, y: 0, w: PDF_CONTENT_WIDTH, h: barHeight, color: '#e5e8ef' });
    }
    stack.push({ canvas: barElements, margin: [0, 0, 0, 14] } as Content);

    // KPI cards row — one column per segment, with a coloured accent on top.
    const cardGap = 12;
    const cardCount = data.segments.length;
    const cardWidth = (PDF_CONTENT_WIDTH - cardGap * (cardCount - 1)) / cardCount;
    stack.push({
      columnGap: cardGap,
      columns: data.segments.map((seg) => ({
        width: cardWidth,
        stack: [
          { canvas: [{ type: 'rect', x: 0, y: 0, w: cardWidth, h: 3, color: seg.color }] } as Content,
          { text: seg.label, fontSize: 9, color: '#707070', margin: [0, 5, 0, 1] },
          { text: seg.value, fontSize: 14, bold: true, color: '#07184D' },
        ],
      })),
    });

    return { stack, pageBreak: 'before' };
  }

  /**
   * Split rows across N side-by-side column groups (column-major). Flat tables
   * are chunked by row count; grouped tables keep each group (header + its
   * indented items) intact and distribute whole blocks to the shortest column
   * so groups never split across columns.
   */
  private distributeRowsIntoColumns(
    rows: StatisticsPdfTableRow[],
    groups: number
  ): StatisticsPdfTableRow[][] {
    if (groups <= 1) {
      return [rows];
    }

    const isGrouped = rows.some((row) => row.group);
    if (!isGrouped) {
      const perColumn = Math.ceil(rows.length / groups);
      return Array.from({ length: groups }, (_, g) =>
        rows.slice(g * perColumn, (g + 1) * perColumn)
      );
    }

    // Build blocks: each group header starts a new block; items attach to it.
    const blocks: StatisticsPdfTableRow[][] = [];
    let current: StatisticsPdfTableRow[] | null = null;
    for (const row of rows) {
      if (row.group || !current) {
        current = [row];
        blocks.push(current);
      } else {
        current.push(row);
      }
    }

    const columns: StatisticsPdfTableRow[][] = Array.from({ length: groups }, () => []);
    for (const block of blocks) {
      let shortest = 0;
      for (let g = 1; g < groups; g++) {
        if (columns[g].length < columns[shortest].length) {
          shortest = g;
        }
      }
      columns[shortest].push(...block);
    }
    return columns;
  }

  private buildSingleImageWidgetPage(
    snapshot: StatisticsExportSnapshot,
    segment: StatisticsPdfImageSegment,
    includeTitle: boolean
  ): Content {
    const stack: Content[] = [];
    if (includeTitle && !snapshot.omitPdfTitle) {
      stack.push({
        text: this.translate.instant(snapshot.titleKey),
        style: 'widgetTitle',
        margin: [0, 0, 0, 8],
      });
    }

    stack.push({
      image: segment.imageDataUrl,
      fit: [segment.pdfWidth, segment.pdfHeight],
    });

    const blockHeight =
      includeTitle && !snapshot.omitPdfTitle
        ? segment.pdfHeight + PDF_WIDGET_TITLE_SPACE
        : segment.pdfHeight;

    return {
      stack,
      pageBreak: 'before',
      unbreakable: blockHeight <= PDF_CONTENT_HEIGHT,
    };
  }

  private resolvePdfSegments(snapshot: StatisticsExportSnapshot): StatisticsPdfImageSegment[] {
    if (snapshot.pdfSegments?.length) {
      return snapshot.pdfSegments;
    }

    return [
      {
        imageDataUrl: snapshot.imageDataUrl,
        pdfWidth: snapshot.pdfWidth ?? PDF_CONTENT_WIDTH,
        pdfHeight: snapshot.pdfHeight ?? PDF_MAX_IMAGE_HEIGHT,
      },
    ];
  }

  private async buildExportOptions(
    context: StatisticsPdfExportContext,
    onProgress?: StatisticsExportProgressCallback
  ): Promise<ReportPdfExportOptions> {
    const title = `${this.translate.instant(TAB_TITLE_KEYS[context.tab])} — ${this.translate.instant('STATISTICS')}`;
    const preparedSnapshots: StatisticsExportSnapshot[] = [];
    const snapshotTotal = context.snapshots.length;

    for (let index = 0; index < snapshotTotal; index++) {
      const snapshot = context.snapshots[index];
      onProgress?.({
        phase: 'prepare',
        current: index + 1,
        total: snapshotTotal,
        messageKey: snapshot.titleKey,
      });
      preparedSnapshots.push(await this.prepareSnapshotForPdf(snapshot, snapshot.scaleToFullPage));
    }
    const widgetPages = this.buildWidgetPages(preparedSnapshots);
    if (!widgetPages.length) {
      throw new Error('No valid widget images prepared for PDF export');
    }

    return {
      title,
      filename: this.buildFilename(context),
      content: [
        ...this.buildTitlePageContent(context),
        ...widgetPages,
      ],
      pageOrientation: 'landscape',
      pageMargins: STATISTICS_PDF_PAGE_MARGINS,
      showPageNumbers: false,
    };
  }

  private async prepareSnapshotForPdf(
    snapshot: StatisticsExportSnapshot,
    scaleToFullPage = false
  ): Promise<StatisticsExportSnapshot> {
    // Natively-rendered widgets carry no image — nothing to prepare.
    if (snapshot.pdfNativeTable || snapshot.avgDurations) {
      return snapshot;
    }

    const imageSource =
      snapshot.pdfNativeLayout === 'emaac-alerts-sidebar'
        ? snapshot.squareChartImageDataUrl ?? snapshot.imageDataUrl
        : snapshot.imageDataUrl;
    const prepared = await this.prepareImageForPdf(imageSource, scaleToFullPage);
    const segments = prepared.segments.filter((segment) =>
      this.isValidImageDataUrl(segment.imageDataUrl)
    );

    if (!segments.length) {
      return this.omitFromPdf(snapshot);
    }

    return {
      ...snapshot,
      imageDataUrl: segments[0].imageDataUrl,
      squareChartImageDataUrl:
        snapshot.pdfNativeLayout === 'emaac-alerts-sidebar'
          ? segments[0].imageDataUrl
          : snapshot.squareChartImageDataUrl,
      pdfWidth: segments[0].pdfWidth,
      pdfHeight: segments[0].pdfHeight,
      pdfSegments: segments,
    };
  }

  /**
   * A widget whose capture produced no renderable image (e.g. a chart/table with
   * no data for the selected range). Clear its image so buildWidgetPages omits
   * the widget instead of throwing and failing the whole export.
   */
  private omitFromPdf(snapshot: StatisticsExportSnapshot): StatisticsExportSnapshot {
    console.warn(
      `[statistics-pdf-export] no preparable image for widget "${snapshot.slug}"; omitting it from the PDF`
    );
    return {
      ...snapshot,
      imageDataUrl: '',
      squareChartImageDataUrl: undefined,
      pdfSegments: undefined,
    };
  }

  private async prepareImageForPdf(
    dataUrl: string,
    scaleToFullPage = false
  ): Promise<{ segments: StatisticsPdfImageSegment[] }> {
    if (!this.isValidImageDataUrl(dataUrl) || typeof document === 'undefined') {
      return { segments: [] };
    }

    return new Promise((resolve) => {
      const image = new Image();

      // Guarantee the promise settles exactly once. Without this, a throw inside
      // onload (canvas getImageData/toDataURL can throw on oversized inputs) or a
      // stalled image load would leave the whole PDF export hung on an unresolved
      // promise with no error surfaced to the user.
      let settled = false;
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const settle = (result: { segments: StatisticsPdfImageSegment[] }) => {
        if (settled) {
          return;
        }
        settled = true;
        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
        }
        resolve(result);
      };

      timeoutId = setTimeout(() => {
        console.warn('[statistics-pdf-export] image load/processing timed out; skipping segment');
        settle({ segments: [] });
      }, PDF_IMAGE_PREPARE_TIMEOUT_MS);

      image.onload = () => {
        try {
          if (image.width <= 0 || image.height <= 0) {
            settle({ segments: [] });
            return;
          }

          const layout = scaleToFullPage
            ? this.computePdfLayoutFitPage(image.width, image.height)
            : this.computePdfLayoutAtWidth(image.width, image.height);

          const targetWidthPx = Math.max(1, Math.round(layout.pdfWidth * PDF_PIXEL_RATIO));
          const targetHeightPx = Math.max(1, Math.round(layout.pdfHeight * PDF_PIXEL_RATIO));
          const widthMatches = Math.abs(image.width - targetWidthPx) <= 4;
          const heightMatches = Math.abs(image.height - targetHeightPx) <= 4;

          if (widthMatches && heightMatches) {
            settle({
              segments: [
                {
                  imageDataUrl: dataUrl,
                  pdfWidth: layout.pdfWidth,
                  pdfHeight: layout.pdfHeight,
                },
              ],
            });
            return;
          }

          const canvas = this.drawScaledImage(image, targetWidthPx, targetHeightPx);

          if (!canvas) {
            settle({
              segments: [
                {
                  imageDataUrl: dataUrl,
                  pdfWidth: layout.pdfWidth,
                  pdfHeight: layout.pdfHeight,
                },
              ],
            });
            return;
          }

          settle({
            segments: this.createVerticalPageSegments(canvas, layout.pdfWidth, layout.pdfHeight),
          });
        } catch (err) {
          console.warn('[statistics-pdf-export] failed to process image for PDF; skipping segment', err);
          settle({ segments: [] });
        }
      };
      image.onerror = () => settle({ segments: [] });
      image.src = dataUrl;
    });
  }

  private createVerticalPageSegments(
    sourceCanvas: HTMLCanvasElement,
    pdfWidth: number,
    pdfHeight: number
  ): StatisticsPdfImageSegment[] {
    if (pdfHeight <= PDF_MAX_IMAGE_HEIGHT) {
      const segment = this.toPdfSegmentFromCanvas(sourceCanvas, pdfWidth);
      return segment ? [segment] : [];
    }

    const segments: StatisticsPdfImageSegment[] = [];
    let remainingPt = pdfHeight;
    let sourceY = 0;

    while (remainingPt > 0) {
      const pageLimit = segments.length === 0 ? PDF_MAX_IMAGE_HEIGHT : PDF_CONTENT_HEIGHT;
      const sliceHeightPt = Math.min(pageLimit, remainingPt);
      let sliceHeightPx = Math.max(
        1,
        Math.round(sourceCanvas.height * (sliceHeightPt / pdfHeight))
      );
      sliceHeightPx = this.adjustSliceHeightToRowBoundary(
        sourceCanvas,
        sourceY,
        sliceHeightPx
      );

      const segmentCanvas = this.sliceCanvasSegmentCanvas(
        sourceCanvas,
        sourceY,
        sliceHeightPx
      );
      const segment = this.toPdfSegmentFromCanvas(segmentCanvas, pdfWidth);

      // Decrement by the actual consumed PDF points so that row-boundary
      // adjustments (which can shorten sliceHeightPx) don't leave unprocessed
      // canvas content at the bottom of a tall export.
      const actualConsumedPt = (sliceHeightPx / sourceCanvas.height) * pdfHeight;
      sourceY += sliceHeightPx;
      remainingPt -= actualConsumedPt;

      if (segment) {
        segments.push(segment);
      } else if (sourceY >= sourceCanvas.height) {
        break;
      }
    }

    return segments;
  }

  private sliceCanvasSegmentCanvas(
    sourceCanvas: HTMLCanvasElement,
    sourceY: number,
    sliceHeightPx: number
  ): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = sourceCanvas.width;
    canvas.height = sliceHeightPx;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (context) {
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(
        sourceCanvas,
        0,
        sourceY,
        sourceCanvas.width,
        sliceHeightPx,
        0,
        0,
        sourceCanvas.width,
        sliceHeightPx
      );
    }
    return canvas;
  }

  private toPdfSegmentFromCanvas(
    canvas: HTMLCanvasElement,
    pdfWidth: number
  ): StatisticsPdfImageSegment | null {
    const trimmed = this.trimCanvasSegmentForPdf(canvas);
    if (!trimmed) {
      return null;
    }
    const outputCanvas = trimmed;
    if (outputCanvas.height < 1) {
      return null;
    }

    const imageDataUrl = outputCanvas.toDataURL('image/png');
    if (!this.isValidImageDataUrl(imageDataUrl)) {
      return null;
    }

    const pdfHeight = outputCanvas.height / PDF_PIXEL_RATIO;
    return {
      imageDataUrl,
      pdfWidth,
      pdfHeight: Math.max(1, pdfHeight),
    };
  }

  /** Exposed for unit tests. */
  adjustSliceHeightToRowBoundary(
    canvas: HTMLCanvasElement,
    sourceY: number,
    proposedSliceHeightPx: number
  ): number {
    const maxY = canvas.height;
    const targetY = Math.min(maxY, sourceY + proposedSliceHeightPx);
    if (targetY >= maxY || proposedSliceHeightPx <= MIN_SEGMENT_CONTENT_HEIGHT_PX) {
      return proposedSliceHeightPx;
    }

    const searchStart = Math.max(sourceY + MIN_SEGMENT_CONTENT_HEIGHT_PX, targetY - SLICE_BOUNDARY_SEARCH_PX);
    let bestY = targetY;
    let lowestDensity = Number.POSITIVE_INFINITY;

    for (let y = searchStart; y <= targetY; y++) {
      const density = this.canvasRowContentDensity(canvas, y);
      if (density < lowestDensity) {
        lowestDensity = density;
        bestY = y;
      }
    }

    const adjustedHeight = bestY - sourceY;
    if (adjustedHeight < MIN_SEGMENT_CONTENT_HEIGHT_PX) {
      return proposedSliceHeightPx;
    }

    return adjustedHeight;
  }

  /** Exposed for unit tests. */
  trimCanvasSegmentForPdf(canvas: HTMLCanvasElement, paddingPx = 8): HTMLCanvasElement | null {
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      return canvas;
    }

    const { width, height } = canvas;
    const imageData = context.getImageData(0, 0, width, height).data;
    let minY = height;
    let maxY = -1;

    for (let y = 0; y < height; y++) {
      if (this.canvasRowHasContent(imageData, width, y)) {
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
      }
    }

    if (maxY < 0) {
      return null;
    }

    const top = Math.max(0, minY - paddingPx);
    const bottom = Math.min(height, maxY + 1 + paddingPx);
    const trimmedHeight = bottom - top;

    if (trimmedHeight < MIN_SEGMENT_CONTENT_HEIGHT_PX && height > trimmedHeight + MIN_SEGMENT_CONTENT_HEIGHT_PX) {
      return null;
    }

    if (top === 0 && bottom === height) {
      return canvas;
    }

    const trimmedCanvas = document.createElement('canvas');
    trimmedCanvas.width = width;
    trimmedCanvas.height = trimmedHeight;
    const trimmedContext = trimmedCanvas.getContext('2d', { willReadFrequently: true });
    if (!trimmedContext) {
      return canvas;
    }

    trimmedContext.fillStyle = '#ffffff';
    trimmedContext.fillRect(0, 0, width, trimmedHeight);
    trimmedContext.drawImage(canvas, 0, top, width, trimmedHeight, 0, 0, width, trimmedHeight);
    return trimmedCanvas;
  }

  private canvasRowHasContent(
    imageData: Uint8ClampedArray,
    width: number,
    y: number
  ): boolean {
    return this.canvasRowContentDensityFromImageData(imageData, width, y) > 0;
  }

  private canvasRowContentDensity(canvas: HTMLCanvasElement, y: number): number {
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      return 0;
    }

    const imageData = context.getImageData(0, y, canvas.width, 1).data;
    return this.canvasRowContentDensityFromImageData(imageData, canvas.width, 0);
  }

  private canvasRowContentDensityFromImageData(
    imageData: Uint8ClampedArray,
    width: number,
    y: number
  ): number {
    let contentPixels = 0;
    for (let x = 0; x < width; x++) {
      const index = (y * width + x) * 4;
      const red = imageData[index];
      const green = imageData[index + 1];
      const blue = imageData[index + 2];
      const alpha = imageData[index + 3];
      if (alpha === 0 || this.isPdfBackgroundPixel(red, green, blue)) {
        continue;
      }

      contentPixels++;
    }

    return contentPixels;
  }

  private isPdfBackgroundPixel(red: number, green: number, blue: number): boolean {
    return isExportBackgroundPixel(red, green, blue);
  }

  private drawScaledImage(
    image: HTMLImageElement,
    outputWidthPx: number,
    outputHeightPx: number
  ): HTMLCanvasElement | null {
    const canvas = document.createElement('canvas');
    canvas.width = outputWidthPx;
    canvas.height = outputHeightPx;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      return null;
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';

    const scale = Math.min(outputWidthPx / image.width, outputHeightPx / image.height);
    const drawWidth = Math.max(1, Math.round(image.width * scale));
    const drawHeight = Math.max(1, Math.round(image.height * scale));
    const offsetX = Math.floor((outputWidthPx - drawWidth) / 2);
    const offsetY = Math.floor((outputHeightPx - drawHeight) / 2);
    context.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);

    return canvas;
  }

  private formatStatisticsDate(date: Date): string {
    return moment(date).utc().format('YYYY-MM-DD');
  }

  private formatStatisticsDateTime(date: Date): string {
    return moment(date).utc().format('YYYY-MM-DD HH:mm');
  }

  /** Exposed for unit tests. pdfmake canvas primitives only accept hex (not rgb()). */
  normalizePdfCanvasColor(color: string): string {
    const trimmed = color.trim();
    if (trimmed.startsWith('#')) {
      return trimmed;
    }

    const rgbMatch = trimmed.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i);
    if (!rgbMatch) {
      return trimmed;
    }

    return (
      '#' +
      [rgbMatch[1], rgbMatch[2], rgbMatch[3]]
        .map((channel) => Number(channel).toString(16).padStart(2, '0'))
        .join('')
        .toUpperCase()
    );
  }

  private buildKpiLines(kpis: StatisticsKpi[]): Content[] {
    if (!kpis.length) {
      return [{ text: '—', style: 'kpiLine' }];
    }
    return kpis.map((kpi) => ({
      text: `${this.translate.instant(kpi.labelKey)}: ${kpi.value}`,
      style: 'kpiLine',
    }));
  }
}
